import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil, Plus } from '@factorialco/f0-react/icons/app'
import {
  JobsAndRatesDataCollectionDocument,
  JobsAndRatesDataCollectionItemFragment,
  RateProcessedDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DeleteJobRoleModal from 'modules/project_management/components/modals/DeleteJobRoleModal'
import { getPeopleCount } from 'modules/project_management/lib/constants'
import { convertCentsToPercentage } from 'modules/project_management/lib/profitabilityCalculations'

import AddJobsAndRatesSidepanel from './AddJobsAndRatesSidepanel'
import EditJobRatesSidepanel from './EditJobRatesSidepanel'

let totalPeople = 0

interface JobsAndRatesDataCollectionProps {
  canManageRates: boolean
  openAddSidepanel?: boolean
  isJobCatalogTreeEnabled: boolean
  setOpenAddSidepanel?: (open: boolean) => void
}

const JobsAndRatesDataCollection = ({
  canManageRates,
  openAddSidepanel,
  isJobCatalogTreeEnabled,
  setOpenAddSidepanel,
}: JobsAndRatesDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [rateIdToDelete, setRateIdToDelete] = useState<string | null>(null)
  const [internalShowAddSidePanel, setInternalShowAddSidePanel] = useState<boolean>(false)
  const [existingResourceJobLevelIds, setExistingResourceJobLevelIds] = useState<string[]>([])

  const [showEditJobRatesSidepanel, setShowEditJobRatesSidepanel] = useState<boolean>(false)

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const { data: rateProcessedData } = useSubscription(RateProcessedDocument)

  useEffect(() => {
    if (rateProcessedData?.projectmanagementRateProcessed?.rate?.id) {
      const processedRate = rateProcessedData.projectmanagementRateProcessed.rate
      if (!processedRate?.processing) {
        setRefreshTrigger((prev) => prev + 1)
      }
    }
  }, [rateProcessedData])

  const showAddSidePanel = openAddSidepanel ?? internalShowAddSidePanel
  const setShowAddSidePanel = setOpenAddSidepanel ?? setInternalShowAddSidePanel
  const [jobLevelInfo, setJobLevelInfo] = useState<{
    name: string
    resourceId: string
    resourceType: string
  }>({
    name: '',
    resourceId: '0',
    resourceType: 'JobCatalog::Level',
  })

  const source = useGraphqlDataCollectionSource(
    {
      document: JobsAndRatesDataCollectionDocument,
      paginationType: 'infinite-scroll',
      totalItemSummary: (totalItems) =>
        `${totalItems} ${i18n.t('common.jobs')}, ${getPeopleCount(totalPeople)}`,
      mapData: (data) => {
        const nodes = data.projectManagement.ratesConnection.nodes

        const ids = nodes
          .map((node) => {
            const resource = node.resource
            return resource && 'id' in resource ? String(resource.id) : null
          })
          .filter((id): id is string => id !== null)

        setExistingResourceJobLevelIds(ids)

        totalPeople = nodes.reduce((sum, node) => {
          return sum + (node.referencedRatesConnection?.totalCount ?? 0)
        }, 0)

        return nodes
      },
      mapVariables: ({ search, sortings }) => ({
        sortOrder: sortings?.[0],
        roleOrLevel: search,
        resourceType: isJobCatalogTreeEnabled ? 'JobCatalog::TreeLevel' : 'JobCatalog::Level',
      }),
      mapPagination: (data) => {
        return {
          total: data.projectManagement.ratesConnection.totalCount ?? 0,
          cursor: data.projectManagement.ratesConnection.pageInfo?.endCursor ?? '0',
          hasNextPage: data.projectManagement.ratesConnection.pageInfo?.hasNextPage ?? false,
        }
      },
      itemActions: (item) => {
        if (!canManageRates) return
        if (item.id === null) return undefined

        return [
          {
            label: i18n.t('project_management.jobs_and_rates.table.options.edit_rate'),
            icon: Pencil,
            onClick: () => {
              const resource = item.resource

              if (!resource) return undefined

              const resourceName =
                ('name' in resource
                  ? resource.name
                  : 'levelName' in resource
                    ? resource.levelName
                    : '') || ''
              const resourceId = 'id' in resource ? resource.id : '0'

              track(trackingEvents.projectManagement.jobsAndRates.clickOnEditRoleRate, {
                origin: 'from_jobs_and_rates_table',
              })
              setJobLevelInfo({
                name: resourceName,
                resourceId: resourceId,
                resourceType: item.resourceType,
              })
              setShowEditJobRatesSidepanel(true)
            },
          },
          {
            label: i18n.t('project_management.jobs_and_rates.table.options.delete_rate'),
            icon: Delete,
            critical: true,
            onClick: () => {
              track(trackingEvents.projectManagement.jobsAndRates.clickOnDeleteFromList)
              setRateIdToDelete(item.id)
              setShowDeleteModal(true)
            },
          },
        ]
      },
      itemUrl: (rate) => {
        const { id, processingRate } = rate

        if (processingRate?.processing) {
          return undefined
        }

        return `${navigation.projects.jobsAndRates.rateId.fullPath({
          rateId: id,
        })}`
      },
      primaryActions: () => {
        if (!canManageRates) return
        return {
          label: i18n.t('project_management.jobs_and_rates.table.main_button'),
          icon: Plus,
          onClick: () => {
            track(trackingEvents.projectManagement.jobsAndRates.clickOnAddJobButton)
            setShowAddSidePanel(true)
          },
        }
      },
      search: {
        enabled: true,
      },
      sortings: {
        charging_cost_cents: {
          label: i18n.t('project_management.jobs_and_rates.table.headers.cost_rate.label'),
        },
        billing_rate_cents: {
          label: i18n.t('project_management.jobs_and_rates.table.headers.bill_rate.label'),
        },
        markup_percentage_cents: {
          label: i18n.t('project_management.jobs_and_rates.table.headers.markup.label'),
        },
      },
    },
    [refreshTrigger]
  )

  return (
    <Box paddingTop="s48">
      <OneDataCollection
        id="project-management-jobs-and-rates/v1"
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('project_management.jobs_and_rates.table.empty_state.title'),
            description: i18n.t('project_management.jobs_and_rates.table.empty_state.description'),
            emoji: '💰',
            actions: [
              {
                label: i18n.t('project_management.jobs_and_rates.table.main_button'),
                icon: Plus,
                onClick: () => {
                  setShowAddSidePanel(true)
                },
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnReordering: true,
              allowColumnHiding: true,
              frozenColumns: 2,
              columns: compact([
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.role.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.role.tooltip_message'
                  ),
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    const resource = item.resource
                    const value =
                      resource && 'ancestor' in resource
                        ? resource.ancestor?.name
                        : resource && 'role' in resource
                          ? resource.role?.name
                          : undefined

                    return {
                      type: 'text',
                      value,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.job_level'),
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    const resource = item.resource
                    const value =
                      resource && 'name' in resource
                        ? resource.name
                        : resource && 'levelName' in resource
                          ? resource.levelName
                          : undefined

                    return {
                      type: 'text',
                      value,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.cost_rate.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.cost_rate.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'charging_cost_cents' as const,
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item.chargingCostCents,
                        currency: item.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.bill_rate.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.bill_rate.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'billing_rate_cents' as const,
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item.billingRateCents,
                        currency: item.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.markup.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.markup.tooltip_message'
                  ),
                  align: 'right',
                  sorting: 'markup_percentage_cents' as const,
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: `${convertCentsToPercentage(item.markupPercentageCents)}%`,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.margin.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.margin.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    if (item.marginPercentageCents == null) {
                      return {
                        type: 'text',
                        value: '-',
                      }
                    }
                    return {
                      type: 'text',
                      value: `${(item.marginPercentageCents / 100).toFixed(2)}%`,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.people'),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    if (item.processingRate?.processing) {
                      return {
                        type: 'status',
                        value: {
                          label: i18n.t('project_management.jobs_and_rates.processing_status'),
                          status: 'neutral',
                        },
                      }
                    } else {
                      return {
                        type: 'number',
                        value: item.referencedRatesConnection?.totalCount ?? 0,
                      }
                    }
                  },
                },
              ]),
            },
          },
        ]}
      />
      {showDeleteModal && rateIdToDelete && (
        <DeleteJobRoleModal
          rateId={rateIdToDelete}
          onClose={() => {
            setShowDeleteModal(false)
            setRateIdToDelete(null)
          }}
        />
      )}
      <AddJobsAndRatesSidepanel
        showAddSidePanel={showAddSidePanel}
        onClose={() => setShowAddSidePanel(false)}
        existingResourceJobLevelIds={existingResourceJobLevelIds}
        isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
      />
      <EditJobRatesSidepanel
        showSidePanel={showEditJobRatesSidepanel}
        onClose={() => setShowEditJobRatesSidepanel(false)}
        jobLevelInfo={jobLevelInfo}
        refetchQueries={[JobsAndRatesDataCollectionDocument]}
      />
    </Box>
  )
}

export default JobsAndRatesDataCollection
